/**
 * Phase 2 — work-order canonical constants.
 *
 * These literal lists power both Postgres enums and Zod parsers. Any
 * additions/removals require a coordinated migration + UI change; the
 * lists are the single source of truth for the cross-process contract.
 */

export const WORK_ORDER_CATEGORIES = [
  'plumbing',
  'electrical',
  'hvac',
  'appliance',
  'structural',
  'pest',
  'cleaning',
  'safety',
  'compliance',
  'inspection_followup',
  'violation_remediation',
  'other',
] as const;
export type WorkOrderCategory = (typeof WORK_ORDER_CATEGORIES)[number];

export const WORK_ORDER_CATEGORY_LABELS: Record<WorkOrderCategory, string> = {
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  hvac: 'HVAC',
  appliance: 'Appliance',
  structural: 'Structural',
  pest: 'Pest control',
  cleaning: 'Cleaning',
  safety: 'Safety',
  compliance: 'Compliance',
  inspection_followup: 'Inspection follow-up',
  violation_remediation: 'Violation remediation',
  other: 'Other',
};

export const WORK_ORDER_SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
export type WorkOrderSeverity = (typeof WORK_ORDER_SEVERITIES)[number];

export const WORK_ORDER_SEVERITY_LABELS: Record<WorkOrderSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export const WORK_ORDER_STATUSES = [
  'submitted',
  'triaged',
  'in_progress',
  'awaiting_vendor',
  'awaiting_parts',
  'on_hold',
  'completed',
  'cancelled',
] as const;
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

export const WORK_ORDER_STATUS_LABELS: Record<WorkOrderStatus, string> = {
  submitted: 'Submitted',
  triaged: 'Triaged',
  in_progress: 'In progress',
  awaiting_vendor: 'Awaiting vendor',
  awaiting_parts: 'Awaiting parts',
  on_hold: 'On hold',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/**
 * State-machine transitions. Read as: from a given source status, the
 * caller may move the work order into any of the listed target statuses.
 *
 * `cancelled` is terminal. `completed` may only be reopened by an admin
 * — encoded by the role gate in `validateTransition`, not by adding
 * `in_progress` to multiple from-states (that's already covered).
 */
export const WORK_ORDER_TRANSITIONS: Record<WorkOrderStatus, readonly WorkOrderStatus[]> = {
  submitted: ['triaged', 'in_progress', 'cancelled'],
  triaged: ['in_progress', 'awaiting_vendor', 'on_hold', 'cancelled'],
  in_progress: ['awaiting_vendor', 'awaiting_parts', 'on_hold', 'completed', 'cancelled'],
  awaiting_vendor: ['in_progress', 'completed', 'cancelled'],
  awaiting_parts: ['in_progress', 'cancelled'],
  on_hold: ['in_progress', 'cancelled'],
  completed: ['in_progress'],
  cancelled: [],
};

/** Statuses considered "open" for filters, dashboards, building-detail badges. */
export const WORK_ORDER_OPEN_STATUSES = WORK_ORDER_STATUSES.filter(
  (s) => s !== 'completed' && s !== 'cancelled',
) as readonly WorkOrderStatus[];

export const WORK_ORDER_SUBMITTED_VIA = [
  'web',
  'mobile',
  'inspection',
  'violation',
  'compliance_overdue',
  'email',
  'voice',
  'api',
] as const;
export type WorkOrderSubmittedVia = (typeof WORK_ORDER_SUBMITTED_VIA)[number];

export const WORK_ORDER_EVENT_KINDS = [
  'comment',
  'status_changed',
  'assigned',
  'unassigned',
  'due_date_changed',
  'severity_changed',
  'photo_added',
  'file_added',
  'vendor_signed_off',
  'linked_to_inspection',
  'linked_to_violation',
  'linked_to_compliance',
  'system_note',
] as const;
export type WorkOrderEventKind = (typeof WORK_ORDER_EVENT_KINDS)[number];

export const WORK_ORDER_ATTACHMENT_KINDS = [
  'photo',
  'document',
  'receipt',
  'signature',
  'voice_memo',
] as const;
export type WorkOrderAttachmentKind = (typeof WORK_ORDER_ATTACHMENT_KINDS)[number];

/** `actor_kind` for a work-order event row. (`user`, `vendor`, `system`.) */
export const WORK_ORDER_ACTOR_KINDS = ['user', 'vendor', 'system'] as const;
export type WorkOrderActorKind = (typeof WORK_ORDER_ACTOR_KINDS)[number];
